(function () {
  const GIFT_VARIANT_ID = 49089263698217;
  const MINIMUM_CENTS = 30000;
  const GIFT_PROPERTY = '_kesser_auto_gift';
  const GIFT_LABEL = 'Compras acima de R$ 300,00';
  const SYNC_DEBOUNCE_MS = 120;
  const SYNC_SAFETY_MS = 5000;

  let lastSignature = '';
  let syncing = false;
  let syncSafetyTimer = null;
  let syncQueue = Promise.resolve();
  let internalCartCallDepth = 0;
  let suppressCartEventSync = false;
  let debounceTimer = null;
  let pendingCartHint = null;

  function injectStyles() {
    if (document.getElementById('kesser-auto-gift-styles')) return;

    const style = document.createElement('style');
    style.id = 'kesser-auto-gift-styles';
    style.textContent = `
      .kesser-auto-gift-line quantity-input .quantity__button,
      .kesser-auto-gift-line .quantity__button {
        display: none !important;
        pointer-events: none !important;
      }

      .kesser-auto-gift-line .quantity__input {
        pointer-events: none !important;
        opacity: 0.65;
        border: 0;
        background: transparent;
        text-align: center;
        max-width: 2.5rem;
      }

      .kesser-auto-gift-line cart-remove-button,
      .kesser-auto-gift-line .cart-remove-button,
      .kesser-auto-gift-line a[href*="quantity=0"] {
        display: none !important;
        pointer-events: none !important;
      }
    `;
    document.head.appendChild(style);
  }

  function isInternalCartCall() {
    return internalCartCallDepth > 0;
  }

  async function cartFetch(url, options) {
    internalCartCallDepth += 1;
    try {
      return await fetch(url, options);
    } finally {
      internalCartCallDepth -= 1;
    }
  }

  function normalizeProperties(properties) {
    if (!properties) return {};

    if (Array.isArray(properties)) {
      return properties.reduce((acc, entry) => {
        const name = entry?.name ?? entry?.first ?? entry?.key;
        const value = entry?.value ?? entry?.last ?? '';
        if (name) acc[name] = value;
        return acc;
      }, {});
    }

    return { ...properties };
  }

  function isGiftVariant(variantId) {
    return Number(variantId) === GIFT_VARIANT_ID;
  }

  function isAutoGiftLine(item) {
    if (!item || !isGiftVariant(item.variant_id)) return false;

    const props = normalizeProperties(item.properties);
    if (String(props[GIFT_PROPERTY]) === '1') return true;
    if (String(props.Brinde || '').includes('300')) return true;
    if (item.final_line_price === 0 || item.price === 0) return true;

    // Gift variant SKU is exclusive to the auto-gift flow.
    return true;
  }

  function findAutoGiftLines(cart) {
    return cart.items.filter(isAutoGiftLine);
  }

  function findAutoGiftLine(cart) {
    return findAutoGiftLines(cart)[0] || null;
  }

  function getSectionsConfig() {
    const sections = [
      { key: 'cart-drawer', selector: '#CartDrawer .drawer__inner' },
      { key: 'cart-icon-bubble', selector: '#shopify-section-cart-icon-bubble, [id*="cart-icon-bubble"]' },
      { key: 'cart-live-region-text', selector: '#shopify-section-cart-live-region-text, [id*="cart-live-region-text"]' },
    ];

    const mainCart = document.getElementById('main-cart-items');
    if (mainCart?.dataset?.id) {
      sections.push({
        key: mainCart.dataset.id,
        selector: '#main-cart-items .js-contents',
      });
    }

    const mainFooter = document.getElementById('main-cart-footer');
    if (mainFooter?.dataset?.id) {
      sections.push({
        key: mainFooter.dataset.id,
        selector: '#main-cart-footer .js-contents',
      });
    }

    return sections;
  }

  function getSectionKeys() {
    return getSectionsConfig().map((section) => section.key);
  }

  function getSubtotalExcludingAutoGift(cart) {
    return cart.items.reduce((sum, item) => {
      if (isAutoGiftLine(item)) return sum;
      return sum + item.final_line_price;
    }, 0);
  }

  function buildSignature(cart) {
    const itemsSig = cart.items
      .map((item) => `${item.key}:${item.variant_id}:${item.quantity}:${item.final_line_price}`)
      .sort()
      .join('|');

    return `${itemsSig}|${cart.total_price}`;
  }

  function qualifiesForGift(cart) {
    return getSubtotalExcludingAutoGift(cart) >= MINIMUM_CENTS;
  }

  function giftIsValid(cart) {
    const autoGifts = findAutoGiftLines(cart);
    if (!qualifiesForGift(cart)) return autoGifts.length === 0;
    return autoGifts.length === 1 && autoGifts[0].quantity === 1;
  }

  function checkoutShouldEnable(cart) {
    if (!cart?.items?.length) return false;

    const nonGiftItems = cart.items.filter((item) => !isAutoGiftLine(item));
    if (!nonGiftItems.length) return false;

    const subtotal = getSubtotalExcludingAutoGift(cart);
    const autoGifts = findAutoGiftLines(cart);

    if (subtotal < MINIMUM_CENTS) {
      return autoGifts.length === 0;
    }

    return autoGifts.length === 1 && autoGifts[0].quantity === 1;
  }

  async function fetchCart() {
    const response = await cartFetch('/cart.js', { cache: 'no-store' });
    return response.json();
  }

  function parseSectionHTML(html, selector) {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const scoped = doc.querySelector(selector);
    if (scoped) return scoped.innerHTML;
    const section = doc.querySelector('.shopify-section');
    return section ? section.innerHTML : html;
  }

  function applySections(sections) {
    if (!sections) return;

    getSectionsConfig().forEach(({ key, selector }) => {
      const html = sections[key];
      if (!html) return;

      const target = document.querySelector(selector);
      if (target) target.innerHTML = parseSectionHTML(html, selector.split(' ').pop());
    });

    suppressCartEventSync = true;
    document.dispatchEvent(new CustomEvent('cart:updated'));
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    suppressCartEventSync = false;

    requestAnimationFrame(() => lockGiftUI());
  }

  async function refreshCartUI() {
    const sections = getSectionKeys();
    if (!sections.length) return null;

    const response = await cartFetch(`/cart/update.js?sections=${sections.join(',')}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    });
    const data = await response.json();
    applySections(data.sections);
    return data;
  }

  function setCheckoutDisabled(disabled, reason = '') {
    document
      .querySelectorAll(
        'button[name="checkout"], a[href="/checkout"], .cart__checkout-button, .drawer__checkout, #CartDrawer-Checkout'
      )
      .forEach((button) => {
        if (disabled) {
          button.setAttribute('disabled', 'disabled');
          button.setAttribute('aria-disabled', 'true');
          button.style.cursor = 'not-allowed';
          button.style.opacity = '0.5';
          button.title = reason;
        } else {
          button.removeAttribute('disabled');
          button.removeAttribute('aria-disabled');
          button.style.cursor = '';
          button.style.opacity = '';
          button.title = '';
        }
      });
  }

  function updateCheckoutState(cart) {
    if (syncing) {
      setCheckoutDisabled(true, 'Atualizando brinde...');
      return;
    }

    const qualifies = qualifiesForGift(cart);
    const autoGift = findAutoGiftLine(cart);
    const onlyAutoGift = cart.items.length === 1 && isAutoGiftLine(cart.items[0]);
    const needsGift = qualifies && !autoGift;
    const invalidGiftQty = qualifies && autoGift && autoGift.quantity !== 1;
    const duplicateAutoGifts = findAutoGiftLines(cart).length > 1;

    if (onlyAutoGift) {
      setCheckoutDisabled(true, 'Adicione produtos para finalizar a compra.');
      return;
    }

    if (needsGift) {
      setCheckoutDisabled(true, 'Adicionando brinde...');
      return;
    }

    if (invalidGiftQty || duplicateAutoGifts) {
      setCheckoutDisabled(true, 'Ajustando brinde...');
      return;
    }

    if (!qualifies && autoGift) {
      setCheckoutDisabled(true, 'Atualizando carrinho...');
      return;
    }

    setCheckoutDisabled(!checkoutShouldEnable(cart), '');
  }

  function clearSyncSafetyTimer() {
    if (syncSafetyTimer) {
      clearTimeout(syncSafetyTimer);
      syncSafetyTimer = null;
    }
  }

  function armSyncSafetyTimer() {
    clearSyncSafetyTimer();
    syncSafetyTimer = setTimeout(() => {
      if (!syncing) return;
      syncing = false;
      fetchCart()
        .then((cart) => updateCheckoutState(cart))
        .catch(() => setCheckoutDisabled(false));
    }, SYNC_SAFETY_MS);
  }

  function finishSync(cart) {
    syncing = false;
    clearSyncSafetyTimer();
    if (cart) {
      updateCheckoutState(cart);
      lockGiftUI(cart);
      return;
    }
    fetchCart()
      .then((freshCart) => {
        updateCheckoutState(freshCart);
        lockGiftUI(freshCart);
      })
      .catch(() => setCheckoutDisabled(false));
  }

  function unlockGiftRows() {
    document.querySelectorAll('.kesser-auto-gift-line').forEach((row) => {
      row.classList.remove('kesser-auto-gift-line');
    });
  }

  function lockGiftUI(cartHint) {
    unlockGiftRows();

    const applyLock = (cart) => {
      cart.items.forEach((item, index) => {
        if (!isAutoGiftLine(item)) return;

        const lineNumber = index + 1;
        const row =
          document.getElementById(`CartDrawer-Item-${lineNumber}`) ||
          document.getElementById(`CartItem-${lineNumber}`);

        if (!row) return;

        row.classList.add('kesser-auto-gift-line');

        row.querySelectorAll('.quantity__input').forEach((input) => {
          input.readOnly = true;
          input.disabled = true;
          input.setAttribute('aria-disabled', 'true');
        });

        row.querySelectorAll('quantity-input .quantity__button, .quantity__button').forEach((button) => {
          button.disabled = true;
          button.setAttribute('aria-hidden', 'true');
        });
      });
    };

    if (cartHint?.items) {
      applyLock(cartHint);
      return;
    }

    fetchCart().then(applyLock).catch(() => {});
  }

  async function addGiftWithSections() {
    const sections = getSectionKeys();
    const payload = {
      items: [
        {
          id: GIFT_VARIANT_ID,
          quantity: 1,
          properties: {
            [GIFT_PROPERTY]: '1',
            Brinde: GIFT_LABEL,
          },
        },
      ],
    };

    if (sections.length) {
      payload.sections = sections.join(',');
      payload.sections_url = window.location.pathname;
    }

    const response = await cartFetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    const data = await response.json();
    if (data.sections) applySections(data.sections);
    return data;
  }

  async function changeCartLine(lineKey, quantity) {
    const response = await cartFetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: lineKey,
        quantity,
        sections: getSectionKeys().join(','),
        sections_url: window.location.pathname,
      }),
    });

    const data = await response.json();
    if (data.sections) applySections(data.sections);
    else await refreshCartUI();
    return data;
  }

  async function syncGift(cart) {
    const qualifies = qualifiesForGift(cart);
    const autoGifts = findAutoGiftLines(cart);

    if (!qualifies) {
      for (const giftLine of autoGifts) {
        await changeCartLine(giftLine.key, 0);
      }
      if (autoGifts.length) return fetchCart();
      return cart;
    }

    if (autoGifts.length > 1) {
      for (let i = 1; i < autoGifts.length; i += 1) {
        await changeCartLine(autoGifts[i].key, 0);
      }
      cart = await fetchCart();
    }

    const autoGift = findAutoGiftLine(cart);

    if (!autoGift) {
      await addGiftWithSections();
      return fetchCart();
    }

    if (autoGift.quantity !== 1) {
      await changeCartLine(autoGift.key, 1);
      return fetchCart();
    }

    return cart;
  }

  function runSync(cartHint) {
    syncQueue = syncQueue
      .then(async () => {
        if (syncing) return;
        syncing = true;
        armSyncSafetyTimer();

        let cart = null;

        try {
          cart = cartHint?.items ? cartHint : await fetchCart();
          const signature = buildSignature(cart);
          const needsSync = signature !== lastSignature || !giftIsValid(cart);

          if (needsSync) {
            cart = await syncGift(cart);
            lastSignature = buildSignature(cart);
          } else {
            lastSignature = signature;
          }
        } catch (error) {
          console.error('Kesser gift sync error:', error);
          try {
            cart = await fetchCart();
          } catch {
            cart = null;
          }
        } finally {
          finishSync(cart);
        }
      })
      .catch(() => {
        finishSync(null);
      });

    return syncQueue;
  }

  function enqueueSync(cartHint) {
    if (cartHint?.items) pendingCartHint = cartHint;

    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      const hint = pendingCartHint;
      pendingCartHint = null;
      runSync(hint);
    }, SYNC_DEBOUNCE_MS);
  }

  async function ensureGiftReady() {
    await runSync();

    const cart = await fetchCart();

    if (qualifiesForGift(cart) && !findAutoGiftLine(cart)) {
      await addGiftWithSections();
      lastSignature = '';
      await runSync();
    }

    return fetchCart();
  }

  function hookFetch() {
    const originalFetch = window.fetch;

    window.fetch = async function (...args) {
      const response = await originalFetch.apply(this, args);
      const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';

      if (/\/cart\/(add|change|update|clear)\.js/.test(url)) {
        if (isInternalCartCall() || suppressCartEventSync) {
          return response;
        }

        response
          .clone()
          .json()
          .then((data) => {
            enqueueSync(data.items ? data : null);
          })
          .catch(() => enqueueSync());
      }

      return response;
    };
  }

  function hookCheckout() {
    document.addEventListener(
      'click',
      async (event) => {
        const checkout = event.target.closest(
          'button[name="checkout"], a[href="/checkout"], .cart__checkout-button, .drawer__checkout, #CartDrawer-Checkout'
        );
        if (!checkout || checkout.disabled) return;

        const cart = await fetchCart();

        if (checkoutShouldEnable(cart)) return;

        if (!qualifiesForGift(cart)) return;

        event.preventDefault();
        event.stopPropagation();

        setCheckoutDisabled(true, 'Adicionando brinde...');
        const readyCart = await ensureGiftReady();
        updateCheckoutState(readyCart);

        if (!checkoutShouldEnable(readyCart)) return;

        if (checkout.tagName === 'A') {
          window.location.href = checkout.href;
          return;
        }

        const form = checkout.closest('form');
        if (form) form.submit();
      },
      true
    );
  }

  function hookCartDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    const observer = new MutationObserver(() => {
      if (drawer.classList.contains('active')) enqueueSync();
    });

    observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  function hookPubSub() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      if (isInternalCartCall() || suppressCartEventSync) return;
      enqueueSync();
    });
  }

  function isCartPage() {
    return window.location.pathname === '/cart' || window.location.pathname.endsWith('/cart');
  }

  async function bootstrapCartPage() {
    await runSync();
    const cart = await fetchCart().catch(() => null);
    if (cart) updateCheckoutState(cart);
  }

  function start() {
    injectStyles();
    hookFetch();
    hookCheckout();
    hookCartDrawer();
    hookPubSub();

    if (isCartPage()) {
      bootstrapCartPage();
    } else {
      enqueueSync();
    }

    document.addEventListener('cart:updated', () => {
      if (suppressCartEventSync || isInternalCartCall()) return;
      enqueueSync();
    });
    document.addEventListener('cart:refresh', () => lockGiftUI());
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) enqueueSync();
    });

    setInterval(() => enqueueSync(), 8000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
